import { DrugStatus } from "@/models/Drug/Drug";
import { DrugProduct, DrugSource } from "@/models/Drug/DrugProduct";
import DrugStatusEnum = DrugStatus.DrugStatusEnum;

export interface CompoundPatEd {
    id: number;
    compoundID: number;
    description: string;
    contraindications: string;
    administration: string;
    missedDoseInstructions: string;
    monitoring: string;
    sideEffects: string;
    storage: string;
}
export class CompoundPatEd {
    constructor(obj?:CompoundPatEd){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundIngredients {
    id: number;
    compoundID: number;
    drugSource: number;
    drugID: number;
    quantity: number;
    unitOfMeasure: string;
    drugName: string;
}
export class CompoundIngredients {
    constructor(obj?:Array<CompoundIngredients>){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundWarningLabels {
    id: number;
    compoundID: number;
    order: number;
    warning: string;
}
export class CompoundWarningLabels {
    constructor(obj?:Array<CompoundWarningLabels>){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundSteps {
    id: number;
    compoundID: number;
    stepNumber: number;
    description: string;
    requireWeight: boolean;
    requirePicture: boolean;
    weightTolerance: number | null;
    expectedWeight: number | null;
}
export class CompoundSteps {
    constructor(obj?:CompoundSteps){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundBatchSteps {
    id: number;
    compoundBatchID: number;
    stepNumber: number;
    description: string;
    expectedWeight: number | null;
    actualWeight: number | null;
    withinTolerance: boolean | null;
    photoId: number | null;
    completed: Date | null;
    completedBy: string | null;
    requirePicture: boolean;
    requireWeight: boolean;
}
export class CompoundBatchSteps {
    constructor(obj?:CompoundBatchSteps){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundBatchIngredients {
    id: number;
    compoundBatchID: number;
    drugID: number;
    drugSource: number;
    quantity: number;
    unitOfMeasure: string;
    lotNumber: string | null;
    expirationDate: Date | null;
    created: Date;
    createdBy: string;
    drugName: string | null;
}
export class CompoundBatchIngredients {
    constructor(obj?:CompoundBatchIngredients){
        if(obj) Object.assign(this, obj);
    }
}

export enum CompoundBatchStatus {
    InProgress = 0,
    Completed = 1,
    Cancelled = 2
}

export interface CompoundBatch {
    id: number;
    batchNumber: string;
    compoundID: number;
    batchSize: number;
    packageSize: number | null;
    lotNumber: string | null;
    beyondUseDate: Date | null;
    storeId: number | null;
    rxNumber: number | null;
    rfNumber: number | null;
    status: CompoundBatchStatus;
    compound: CompoundDrug | null;
    steps: CompoundBatchSteps[] | null;
    ingredients: CompoundBatchIngredients[] | null;
}
export class CompoundBatch {
    constructor(obj?:CompoundBatch){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundDrug extends DrugProduct {
    compoundID?: number;
    ndc: string;
    name: string;
    labelName: string | null;
    awp: number;
    packageSize: number;
    unitOfMeasure: string;
    formulaSize: number | null;
    minimumDispenseQuantity: number;
    deaClass: string;
    instructions: string;
    status: DrugStatusEnum;
    patEd: CompoundPatEd;
    ingredients: Array<CompoundIngredients>;
    warningLabels: Array<CompoundWarningLabels> | null;
    steps: Array<CompoundSteps> | null;
}

export class CompoundDrug extends DrugProduct {
    constructor(compoundId?:number|null, obj?:CompoundDrug|DrugProduct){
        super(obj);
        this.compoundID = compoundId || 0;
        this.drugId = Number(this.compoundID);
        // this.status = new DrugStatus()
        this.activeStatus = DrugStatusEnum.Inactive;
        this.source = DrugSource.CompoundProduct;

        if(obj){
            Object.assign(this, obj)
            this.compoundID = obj.drugId;
        }
    }
    toString(): string {
        const productName = this.name;
        let preferred = '';
        if (this.status == DrugStatusEnum.ActiveAndPreferred) {
            preferred = '\u2B50 ';
        }
        if (productName)
            return this.ndc ? `${preferred}(NG) CompoundID:${this.compoundID}, NDC:${this.ndc} - ${productName} ${this.packageSize ? ("- PS: " + this.packageSize) : ""}${this.quantityOnHand ? ' - QTY:'+this.quantityOnHand : ''}`
                : productName;
        else return '';
    }

    set activeStatus(val: DrugStatusEnum) {
        this.status = val;
    }

    get activeStatus(): DrugStatusEnum {
        return this.status;
    }

    get productNameShort():string {
        return this.name;
    }
    set productNameShort(val: string) {
        this.name = val;
    }

    get isCII():boolean{
        return this.deaClass == "schedule II";
    }

    get NDCFormat542():string{
        return CompoundDrug.formatNDC542(this.ndc);
    }

    static formatNDC542(value:string | null) : string {
        if(!value) return '';
        return `${value.substr(0, 5)}-${value.substr(5, 4)}-${value.substr(9)}`;
    }
}

export namespace CompoundDrug {
    export class EquivalentCompoundDrug{
        compoundID? : number;
        ndc? : string;
        productName? : string;
        packageSize?: number;
    }
}
